import React, { useCallback } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { StatusBadge } from "../StatusBadge";
import { lastDay } from "../../utils";

const weekDays = [
  { short: "Dom", long: "Domingo" },
  { short: "Seg", long: "Segunda-feira" },
  { short: "Ter", long: "Terca-feira" },
  { short: "Qua", long: "Quarta-feira" },
  { short: "Qui", long: "Quinta-feira" },
  { short: "Sex", long: "Sexta-feira" },
  { short: "Sab", long: "Sabado" },
];

const statusAriaLabels = {
  free: "livre",
  partial: "parcial",
  busy: "ocupado",
};

const getDayStatus = (appointmentsMinutes, scheduleMinutes, jobTime) => {
  if (!scheduleMinutes) return "busy";
  const remaining = scheduleMinutes - appointmentsMinutes;
  if (remaining < jobTime) return "busy";
  if (appointmentsMinutes > 0) return "partial";
  return "free";
};

export const PatientCalendarGrid = ({
  isAdmin = false,
  data = [],
  month,
  year,
  workDays = [],
  jobTime,
  daysOfWeek,
  sumHours,
  onSelect,
}) => {
  const firstDayOfMonth = new Date(year, month, 1);
  const lastDayOfMonth = lastDay(firstDayOfMonth).getDate();

  const daysList = Array.from({ length: lastDayOfMonth }, (_, i) => new Date(year, month, i + 1));

  const firstDayOffset = firstDayOfMonth.getDay();
  const skipList = Array.from({ length: firstDayOffset }, (_, i) => i);

  const today = new Date();

  const handleCellClick = useCallback(
    (day) => (e) => {
      e.stopPropagation();
      onSelect && onSelect(day);
    },
    [onSelect]
  );

  return (
    <Box>
      {/* Header dias da semana */}
      <Box
        aria-label="Cabecalho dos dias da semana"
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          textAlign: "center",
          mb: 0.5,
        }}
      >
        {weekDays.map((d) => (
          <Typography
            key={d.short}
            aria-label={d.long}
            variant="caption"
            sx={{ fontWeight: "bold", color: "text.secondary", py: 0.5 }}
          >
            {d.short}
          </Typography>
        ))}
      </Box>

      {/* Grid de dias */}
      <Box
        aria-label="Grade mensal do calendario"
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          border: "1px solid #e0e0e0",
          borderRadius: 1,
        }}
      >
        {/* Células vazias para offset */}
        {skipList.map((i) => (
          <Box
            key={`skip-${i}`}
            aria-hidden="true"
            sx={{
              p: 1,
              borderBottom: "1px solid #e0e0e0",
              borderRight: "1px solid #e0e0e0",
            }}
          />
        ))}

        {/* Dias do mês */}
        {daysList.map((day) => {
          const isToday = day.toDateString() === today.toDateString();
          const isPast = day < new Date(today.getFullYear(), today.getMonth(), today.getDate());
          const dateStr = day.toISOString().split("T").shift();

          const appointmentsMinutes =
            data.filter((x) => x.date === dateStr).reduce(sumHours, {}).amount || 0;

          const dayName = daysOfWeek[day.getDay()];
          const scheduleMinutes =
            workDays.filter((x) => x.day === dayName).reduce(sumHours, {}).amount || 0;

          const status = getDayStatus(appointmentsMinutes, scheduleMinutes, jobTime);
          const isEnabled = isAdmin || (status !== "busy" && !isPast);
          const dateLabel = day.toLocaleDateString("pt-BR", {
            day: "numeric",
            month: "long",
            year: "numeric",
          });
          const dayAriaLabel = `Dia ${dateLabel}, ${statusAriaLabels[status]}`;

          return (
            <Box
              key={day.getDate()}
              aria-label={dayAriaLabel}
              onClick={isEnabled ? handleCellClick(day) : undefined}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                p: 0.5,
                minHeight: 56,
                borderBottom: "1px solid #e0e0e0",
                borderRight: "1px solid #e0e0e0",
                cursor: isEnabled ? "pointer" : "default",
                opacity: isPast ? 0.4 : 1,
                "&:hover": isEnabled ? { bgcolor: "action.hover" } : {},
              }}
            >
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "50%",
                  ...(isToday
                    ? { border: "2px solid", borderColor: "primary.main" }
                    : {}),
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: isToday ? "bold" : "normal" }}>
                  {day.getDate()}
                </Typography>
              </Box>
              {scheduleMinutes > 0 && <StatusBadge status={status} />}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
